/**
 * Datentypen und Aufrufe des Moduls „Fragebögen" (Questionnaire-Autofill).
 *
 * Gegenstück: `app/questionnaires/router.py`. Die Typen hier sind die
 * Antwortschemata dieses Routers und sonst nichts — wer ein Feld vermisst,
 * ergänzt es dort zuerst.
 *
 * Alle Pfade liegen unter `/api/…`: die Kante vor der Prod-URL routet nach
 * einer Präfix-Whitelist, in der `/api` steht (`docs/atlas-agent-loop.md`).
 * Ein Pfad ausserhalb wäre lokal grün und in Prod tot. Aus demselben Grund
 * heisst die SEITE `/fragebogen` und nicht `/questionnaires`: SPA-Routen
 * dürfen nicht wie API-Präfixe aussehen (`App.tsx`, `/termin` vs. `/calendar`).
 */
import { api, apiUrl, authToken, postForm } from "../api";

export type FragebogenStatus =
  | "uploaded"
  | "profiling"
  | "extracting"
  | "matching"
  | "review"
  | "approved"
  | "filled"
  | "rejected"
  | "error";

/** Der Zustand einer Frage im Review (`q_question.review_state`). */
export type ReviewState =
  | "open"
  | "accepted"
  | "edited"
  | "decision_pending"
  | "skipped";

/** Die fachliche Einordnung, die der Server aus `suggestion_json` ableitet. */
export type FrageArt =
  | "suggestion"
  | "decision"
  | "gap"
  | "not_applicable"
  | "offen";

export type Konfidenz = "high" | "medium" | "low";

/**
 * Zwischenstand der laufenden Matching-Stufe („23 von 70").
 *
 * Optional, weil er erst existiert, sobald die Stufe ihn gemeldet hat — und
 * weil ein Server ohne dieses Paket ihn nicht kennt. Fehlt er, zeigt die
 * Oberfläche die Stufe ohne Zähler; ein `0/0` wäre eine erfundene Zahl.
 */
export interface LaufFortschritt {
  erledigt: number;
  gesamt: number;
}

export interface Fragebogen {
  id: string;
  filename: string;
  mime: string;
  customer: string | null;
  product_slug: string | null;
  status: FragebogenStatus;
  question_count: number;
  error_text: string | null;
  created_by: string;
  created_at: string;
  fortschritt?: LaufFortschritt | null;
}

export interface FragebogenDetail extends Fragebogen {
  format_profile: FormatProfil | null;
  pipeline_state: PipelineState;
  questions: unknown[];
}

/**
 * Das Format-Profil aus `app/questionnaires/profiler.py::FormatProfile`
 * (`to_json()`), so wie es `pipeline.py:130` auf der Zeile ablegt.
 *
 * Nur `unsupported` ist hier ausgeschrieben, weil die Oberfläche NUR das liest:
 * bekannte Fähigkeits-Lücken eines angenommenen Bogens (Excel-Formular-
 * steuerelemente, die openpyxl nicht sieht; flaches PDF ohne Formularfelder).
 * Beide lassen den Bogen normal durch die Pipeline — ohne diesen Hinweis
 * erfährt die prüfende Person nie, dass ein Teil des Dokuments gar nicht
 * erst zur Frage werden konnte.
 *
 * Die Meldungen kommen fertig formuliert (deutsch) vom Server; der Katalog
 * trägt nur die Überschrift drumherum, nicht den Text selbst.
 */
export interface FormatProfil {
  unsupported?: string[];
  [k: string]: unknown;
}

/** Zwischenstände der Pipeline (`pipeline_state_json`) — nur die Schlüssel,
 *  die die Oberfläche wirklich liest. */
export interface PipelineState {
  extraction?: {
    sections?: string[];
    sections_not_applicable?: string[];
    section_reasons?: Record<string, string>;
    questions?: number;
    native_units?: number;
  };
  matching?: {
    stand?: string;
    vorschlaege?: number;
    entscheidungen?: number;
    ohne_antwort?: number;
    konfidenz?: Record<string, number>;
    fehlende_fakten?: string[];
    synthese_modell?: string;
    mindest_score?: number;
    /** Gegen wie viele Registry-Belege die Beleg-Fragen geprueft wurden (K6).
     *  Ein Bogen ohne Produktbezug sieht nur die globalen. */
    anlagen_registry?: number;
    /** Fragen, die NICHT aus Retrieval + Synthese beantwortet wurden (K6). */
    deterministisch?: {
      anlagen?: number;
      gating?: number;
      signoff?: number;
      sektion?: number;
    };
  };
  /** Derselbe Zähler wie {@link Fragebogen.fortschritt}, an der Quelle. */
  fortschritt?: LaufFortschritt;
  matching_unavailable?: string;
  rejected_at_stage?: string;
  [k: string]: unknown;
}

/** Herkunfts-Vorbehalt zu einem interpolierten Fakt (synthesis.py:101-118). */
export interface FaktenVorbehalt {
  key: string;
  /** „Stand"-Datum des Faktenblatt-Werts (ISO), falls erfasst. */
  stand?: string | null;
  /** Offener Erhebungsauftrag ans QM (Konzept §7, „Daten-TODO"). */
  todo?: string | null;
}

/** `suggestion_json` — je nach Lage eine von vier Formen (matching.py). */
export interface Vorschlag {
  answer?: string;
  answer_long?: string;
  confidence?: Konfidenz;
  sources?: string[];
  library_id?: string;
  library_seed_id?: string;
  answer_type?: string;
  /** yes_no_text (Ja/Nein + geforderte Angabe in EINER Zelle): das reine
   *  Ja/Nein fürs Kästchen und die Spezifikation für das Textfeld. `answer`
   *  trägt beides zusammengesetzt („Yes — 2-8 °C"). */
  ja_nein?: string;
  spezifikation?: string;
  /** „Do you have X? If yes, attach Y": neben dem Ja/Nein hängt ein Beleg-
   *  Marker — WELCHER Beleg beizulegen ist und in welchem Zustand
   *  (`anlage_beilegen` | `anlage_vertraulich` | `anlage_abgelaufen` |
   *  `anlage_fehlt` | `anlage_unsicher`). `anlage` trägt Titel/Stand. */
  anlage_marker_code?: string;
  anlage_titel?: string;
  fehlende_fakten?: string[];
  /** ``{key, stand, todo}`` je benutztem Fakt (synthesis.py::vorbehalt) —
   *  KEINE Zeichenketten. Konzept §6: der Reviewer muss das Alter eines
   *  Stammdatums sehen, bevor er es unterschreibt. */
  fakten_vorbehalte?: FaktenVorbehalt[];
  verworfene_quellen?: string[];
  /** Zitierte Bibliothekseinträge, die noch auf `draft` stehen (Konzept B3 /
   *  T9-Befund D-5). Sie kappen die Konfidenz auf `medium`, schliessen den
   *  Vorschlag von der Sammelübernahme aus und tragen im Review ein
   *  „Entwurf"-Zeichen an der Quelle. */
  quellen_nicht_freigegeben?: string[];
  /** Warum die Konfidenz gekappt wurde — im Klartext, damit eine Kappung nicht
   *  wie ein zufällig vorsichtiges Modell aussieht. */
  konfidenz_grund?: string;
  /** Derselbe Grund maschinenlesbar (Muster `grund_code`). Wo er steht, bildet
   *  die Oberfläche den Satz aus ihrem Katalog — `konfidenz_grund` ist deutsch,
   *  weil er auch in Export und Log geht. Die Synthese-Kappungen aus T9 haben
   *  noch keinen Code; ihr deutscher Satz ist der sichtbare Rest davon. */
  konfidenz_grund_code?: "anlage_zuordnung_mittel" | "stammdaten_offener_todo" | string;
  /** Spaltenfragen: die Aussage der Antwortspalte („On site") und ob eine
   *  zitierte Quelle sie belegt. */
  spalten_aussage?: string;
  spalte_belegt?: boolean;
  format_warnung?: string;
  /** Lücke */
  no_answer?: boolean;
  grund?: string;
  /** Norm-Guard (synthesis.py::_norm_konflikt): die Frage fragt nach einer
   *  anderen Norm als die zitierte Quelle. Die beiden Listen stehen hier, damit
   *  die Oberfläche den Satz selbst bilden kann — `grund` ist deutsch (er geht
   *  auch in Export und Log) und wäre in der englischen Maske ein Fremdkörper. */
  norm_konflikt?: { frage: string[]; quelle: string[] };
  bester_score?: number;
  mindest_score?: number;
  naechster_kandidat?: { seed_id?: string; question_en?: string; score?: number } | null;
  /** Entscheidung */
  decision_required?: boolean;
  decision_context?: string;
  kanon_frage?: string;
  verweist_auf?: string[];
  /** ausgeklammerte Sektion */
  section_not_applicable?: boolean;
  section?: string;
  /**
   * Maschinenlesbarer Grund (K6, Muster `norm_konflikt`). Die Oberfläche ist
   * zweisprachig; `grund` ist deutsch, weil er auch in Export und Log geht.
   * Wer einen Satz ZEIGT, nimmt diesen Code und den Katalog.
   */
  grund_code?:
    | "anlage_fehlt"
    | "anlage_unsicher"
    | "anlage_abgelaufen"
    | "anlage_vertraulich"
    | "gating_elternantwort"
    | "gating_sektion"
    | "signoff_signatur"
    | "signoff_titel_fehlt"
    | "signoff_kein_nutzer"
    | string;
  /** Beleg-Frage aus der Anlagen-Registry (`app/questionnaires/anlagen.py`). */
  anlage?: AnlagenBefund;
  /** Abhängigkeit von einer anderen Antwort (`app/questionnaires/gating.py`). */
  gating?: {
    typ?: string;
    trigger?: string;
    eltern_position?: number;
    eltern_antwort?: string;
    eltern_text?: string;
    praefix?: string;
  };
  /**
   * Stammdaten direkt aus dem Faktenblatt (`app/questionnaires/stammdaten.py`).
   *
   * `wert` ist der ungekürzte Faktenblatt-Wert, `answer` die daraus gebildete
   * Feldantwort — sie können auseinanderfallen („1993" → „Since 1993",
   * „approx. 75" → „75" in einem Zahlenfeld), und der Prüfer muss sehen, WAS
   * umgeformt wurde.
   */
  stammdaten?: {
    quelle?: "faktenblatt";
    key?: string;
    familie?: string;
    format?: string;
    stand?: string | null;
    wert?: string;
  };
  /**
   * B3: SF-Umsatz-Empfehlung für die „Annual Revenue"-Entscheidung
   * (`app/questionnaires/sf_revenue.py`). Quelle `scenario_master`: die drei
   * Current-RFC-Linien summiert, EUR→USD umgerechnet, als konkrete Zahl in
   * `empfehlung`. Fallback `faktenblatt`: der hinterlegte Umsatzwert.
   */
  umsatz?: {
    quelle: "scenario_master" | "faktenblatt";
    jahr?: number;
    summe_eur?: number;
    summe_usd?: number;
    /** Konkrete USD-Empfehlung (bzw. Faktenblatt-Wert im Fallback). */
    empfehlung?: string | null;
    basis?: string;
  };
  /** Sign-off aus dem Systemzustand (`app/questionnaires/signoff.py`). */
  signoff?: {
    quelle?: string;
    feld?: string;
    fuelldatum?: string;
    format?: string;
    name?: string;
    titel?: string | null;
    bewusst_leer?: boolean;
  };
  [k: string]: unknown;
}

/** Ein Registry-Eintrag, wie ihn der Server an der Frage mitliefert. */
export interface AnlagenKandidat {
  id: string;
  seed_id: string;
  type: string;
  title: string;
  product_slug: string | null;
  valid_until: string | null;
  latest: boolean;
  score: number;
  /** Read-only-Link zur Beleg-Datei in Salesforce (Mig 0104) — nur Anzeige. */
  sf_url: string | null;
}

/**
 * Das Ergebnis des Registry-Abgleichs für EINE Beleg-Frage.
 *
 * Er kommt vom Server (`anlagen.abgleich`) und wird im Browser NICHT noch
 * einmal gerechnet: dieselbe Regel in zwei Sprachen wäre die Stelle, an der
 * beide auseinanderlaufen — und die Antwort im Kundendokument hängt an der
 * Server-Fassung.
 */
export interface AnlagenBefund {
  quelle: "registry";
  lage: "vorhanden" | "pruefen" | "fehlt";
  stichtag: string;
  bester: AnlagenKandidat | null;
  /** Die tatsächlich beizulegenden Belege — im Regelfall der eine beste, bei
   *  von der Antwort benannten Mehrfach-Belegen (z. B. „13485 and 14001")
   *  mehrere (WH 2026-08-21, #23). Ältere Läufe kennen das Feld nicht → `bester`. */
  beizulegen?: AnlagenKandidat[];
  weitere: AnlagenKandidat[];
  vertraulich: boolean;
  abgelaufen: boolean;
}

export interface FinaleAntwort {
  answer?: string;
  answer_long?: string;
  /** yes_no_text: getrennt für Kästchen (`ja_nein`) und Textfeld
   *  (`spezifikation`); `answer` bleibt die zusammengesetzte Anzeige. */
  ja_nein?: string;
  spezifikation?: string;
  herkunft?: "vorschlag" | "korrektur" | "entscheidung" | string;
  notiz?: string | null;
  folgefragen_gelten?: boolean;
  not_applicable?: boolean;
  grund?: string;
  entschieden_durch?: string;
  von?: string;
  am?: string;
  [k: string]: unknown;
}

export interface Option {
  label?: string;
  [k: string]: unknown;
}

export interface Frage {
  id: string;
  position: number;
  section: string | null;
  section_applicable: boolean;
  question_text: string;
  answer_type: string;
  options: (Option | string)[];
  anchor: Record<string, unknown> | null;
  suggestion: Vorschlag | null;
  review_state: ReviewState;
  final_answer: FinaleAntwort | null;
  kind: FrageArt;
  confidence: Konfidenz | null;
  anchor_kind: string | null;
  library_id: string | null;
  library_seed_id: string | null;
  sources: string[];
  /** Wovon die Frage abhängt (`q_question.gating_json`, Migration 0103) —
   *  auch dann, wenn das Gate NICHT gegriffen hat. */
  gating: {
    typ?: string;
    trigger?: string;
    eltern_position?: number;
    eltern_text?: string;
    praefix?: string;
  } | null;
}

/**
 * Eine Antwortstelle des Originaldokuments, auf die KEINE Frage zeigt.
 *
 * T9 §4/Defekt D-3: im Intertek-Bogen sind das 12 CONTACTS-Zellen und 18 der
 * 24 Zellen in „Locations of activities". Sie werden maschinell nie befüllt.
 * Der Prüfer las trotzdem „Alles bearbeitet" — deshalb liefert der Server sie
 * jetzt mit, und die Oberfläche zeigt sie an.
 */
export interface OffeneStelle {
  /** „docx_table" | „docx_paragraph" — die Art der Fundstelle. */
  art: string;
  /** Menschenlesbare Adresse, z. B. „CONTACTS · Zeile 3 · Name". */
  bezeichnung: string;
}

export interface Fortschritt {
  gesamt: number;
  anwendbar: number;
  reviewt: number;
  offen: number;
  entscheidungen_offen: number;
  luecken: number;
  nicht_anwendbar: number;
  /** Anzahl der {@link OffeneStelle}n. Optional, weil ein Server ohne das
   *  D-3-Paket sie nicht kennt — die Oberfläche liest deshalb `?? 0`. Ein
   *  fehlendes Feld heisst „unbekannt", nie „es gibt keine". */
  offene_stellen?: number;
}

/** Die vier Felder eines Kontakts (`app/questionnaires/kontakt.py::FELDER`). */
export type KontaktFeld = "name" | "titel" | "email" | "telefon";

/**
 * Eine Kontaktfrage, wie das Review sie STELLT — kein Vorschlag.
 *
 * „Wer ist der Ansprechpartner Qualität" hat kein „richtig laut Bibliothek".
 * Der Server liefert deshalb nur die zuletzt gepflegten Faktenblatt-Werte als
 * `vorgaben`; bestätigt oder überschrieben werden sie von einem Menschen.
 */
export interface KontaktVorlage {
  rolle: string;
  /** Welche Felder abgefragt werden. */
  felder: KontaktFeld[];
  /** Welche davon in DIESES Formularfeld geschrieben werden. */
  ziel_felder: KontaktFeld[];
  /** Vorbelegung je Feld. Ein Feld ohne Faktenblatt-Wert fehlt hier — es gibt
   *  keinen Platzhalter, damit „nicht hinterlegt" nicht wie ein Wert aussieht. */
  vorgaben: Partial<Record<KontaktFeld, string>>;
  /** Faktenblatt-Schlüssel ohne Wert (`contact.quality.telefon`). */
  fehlende_fakten: string[];
}

/**
 * Ein aktiver Mitarbeiter aus dem Verzeichnis (B2) — Auswahl für die
 * Kontakt-Entscheidungen. Quelle ist die lokale `users`-Tabelle (nächtlich aus
 * Salesforce gespiegelt); der Reviewer wählt eine Person und die Felder werden
 * auf die Kontaktfelder verteilt.
 */
export interface KontaktVerzeichnisEintrag {
  id: string;
  name: string;
  titel: string | null;
  email: string;
  telefon: string | null;
  department: string | null;
}

export interface FragenAntwort {
  questions: Frage[];
  /** Bibliotheks-Kennung → kanonische Frage. Speist die Quellen-Chips. */
  kanon: Record<string, string>;
  fortschritt: Fortschritt;
  /** Dieselbe Vorsichtsregel wie bei `Fortschritt.offene_stellen`. */
  offene_stellen?: OffeneStelle[];
  /** Frage-Id → Kontakt-Abfrage. Fehlt bei einem Server ohne das
   *  Kontaktrollen-Paket; die Oberfläche liest deshalb `?? {}`. */
  kontakt_vorlagen?: Record<string, KontaktVorlage>;
}

/** Eine vom Reviewer diesem Lauf beigelegte Datei (Mig 0105, Drag&Drop). */
export interface RunAnlage {
  id: string;
  questionnaire_id: string;
  question_id: string | null;
  filename: string;
  mime: string;
  uploaded_by: string;
  created_at: string;
}

export interface Anlage {
  id: string;
  seed_id: string;
  type: string;
  title: string;
  product_slug: string | null;
  topic_tags: string[];
  source_path: string;
  minio_key: string | null;
  issued: string | null;
  valid_until: string | null;
  latest: boolean;
  supersedes: string[];
  notes: string | null;
  sf_url: string | null;
  /** Ob der Beleg beim Abgleich gezogen wird (Mig 0106). `false` = kuratiert
   *  stillgelegt, bleibt aber in der Liste. */
  active: boolean;
}

/** Kuratierbare Felder eines Registry-Eintrags (nur diese vier). Ausgelassene
 *  Felder bleiben unverändert; `valid_until: null` = kein Ablauf. */
export interface RegistryPatch {
  sf_url?: string | null;
  valid_until?: string | null;
  latest?: boolean;
  active?: boolean;
}

const BASIS = "/api/questionnaires";

export const fragebogenApi = {
  liste: () => api.get<Fragebogen[]>(`${BASIS}/?limit=100`),
  detail: (id: string) => api.get<FragebogenDetail>(`${BASIS}/${id}`),
  loeschen: (id: string) => api.delete(`${BASIS}/${id}`),
  fragen: (id: string) => api.get<FragenAntwort>(`${BASIS}/${id}/questions`),
  frageSetzen: (
    id: string,
    frageId: string,
    body: { review_state: ReviewState; final_answer?: FinaleAntwort | null }
  ) => api.patch<Frage>(`${BASIS}/${id}/questions/${frageId}`, body),
  /** `entscheidung` (Freitext) ODER `kontakt` — der Server verlangt genau eines.
   *  Den Text aus den Kontaktfeldern setzt der Server zusammen, nicht wir: er
   *  weiss aus dem Anker, welche Felder in diese Zelle gehören. */
  entscheiden: (
    id: string,
    frageId: string,
    body: {
      entscheidung?: string;
      kontakt?: Partial<Record<KontaktFeld, string>>;
      folgefragen_gelten: boolean;
      notiz?: string | null;
    }
  ) =>
    api.post<{ frage: Frage; folgefragen: string[]; folgefragen_gelten: boolean }>(
      `${BASIS}/${id}/questions/${frageId}/decision`,
      body
    ),
  neuSuchen: (id: string, frageId: string) =>
    api.post<Frage>(`${BASIS}/${id}/questions/${frageId}/rematch`),
  sichereUebernehmen: (id: string, konfidenzen: Konfidenz[]) =>
    api.post<{ uebernommen: number }>(`${BASIS}/${id}/questions/bulk-accept`, {
      konfidenzen,
    }),
  /** B8: „Alles Übrige abhaken" — offene Vorschläge jeder Konfidenz → geprüft,
   *  restliche offene Fragen → ausgelassen; Entscheidungen bleiben offen. */
  alleAbhaken: (id: string) =>
    api.post<{ uebernommen: number; uebersprungen: number }>(
      `${BASIS}/${id}/questions/bulk-finalize`
    ),
  /** B2: aktive Mitarbeiter als Auswahl für die Kontakt-Entscheidungen. */
  kontaktVerzeichnis: () =>
    api.get<KontaktVerzeichnisEintrag[]>(`${BASIS}/contact-directory`),
  sektionSetzen: (id: string, section: string | null, applicable: boolean) =>
    api.post<{ betroffen: number }>(`${BASIS}/${id}/sections/applicable`, {
      section,
      applicable,
    }),
  anlagen: (productSlug: string | null) =>
    api.get<Anlage[]>(
      `${BASIS}/attachment-registry?latest=true` +
        (productSlug ? `&product_slug=${encodeURIComponent(productSlug)}` : "")
    ),
  /** Die vollständige kuratierte Dokumentenliste — inkl. inaktiver Einträge
   *  (Registerkarte „Dokumente"). */
  registry: () => api.get<Anlage[]>(`${BASIS}/attachment-registry`),
  /** Kurations-Felder eines Belegs ändern (SF-Link, Gültigkeit, aktuell, aktiv). */
  registryKuratieren: (id: string, patch: RegistryPatch) =>
    api.patch<Anlage>(`${BASIS}/attachment-registry/${id}`, patch),
  /** POST /fill — schreibt die freigegebenen Antworten ins Originaldokument
   *  (Writer P3/P4). Gibt den Füll-Bericht zurück (gestempelt/übersprungen);
   *  die gefüllte Datei liegt danach unter {@link gefuelltPfad}. */
  ausfuellen: (id: string) => api.post<FuellBericht>(`${BASIS}/${id}/fill`),
  /** Vom Reviewer diesem Lauf beigelegte Anlagen (Drag&Drop). */
  runAnlagen: (id: string) => api.get<RunAnlage[]>(`${BASIS}/${id}/attachments`),
  runAnlageHochladen: (id: string, datei: File, questionId?: string) => {
    const fd = new FormData();
    fd.append("file", datei);
    if (questionId) fd.append("question_id", questionId);
    return postForm<RunAnlage>(`${BASIS}/${id}/attachments`, fd);
  },
  runAnlageLoeschen: (id: string, anlageId: string) =>
    api.delete(`${BASIS}/${id}/attachments/${anlageId}`),
};

/** Ergebnis von POST /fill — wie viele Antwortstellen ins Original geschrieben
 *  wurden. `uebersprungen`/`fehler` = Stellen, die von Hand zu füllen sind. */
export interface FuellBericht {
  geschrieben: number;
  uebersprungen: number;
  fehler: number;
  [k: string]: unknown;
}

/** GET /filled — das ausgefüllte Originaldokument (erst nach POST /fill). */
export function gefuelltPfad(id: string): string {
  return `${BASIS}/${id}/filled`;
}

/** GET /filled/preview — die gefüllte Datei als PDF zur INLINE-Anzeige. */
export function vorschauPfad(id: string): string {
  return `${BASIS}/${id}/filled/preview`;
}

/** GET /package — ZIP aus gefülltem Dokument + Lauf-Anlagen. */
export function paketPfad(id: string): string {
  return `${BASIS}/${id}/package`;
}

/**
 * Wie {@link ladeDatei}, aber gibt eine Blob-URL zurück, statt einen Download
 * anzustoßen — für die Inline-Vorschau in einem `<iframe>`. Der Aufrufer muss
 * die URL später mit `URL.revokeObjectURL` freigeben.
 */
export async function ladeBlobUrl(
  pfad: string
): Promise<{ ok: boolean; status: number; url?: string; text?: string }> {
  const token = authToken();
  const r = await fetch(apiUrl(pfad), {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!r.ok) {
    return { ok: false, status: r.status, text: await r.text().catch(() => "") };
  }
  const blob = await r.blob();
  return { ok: true, status: r.status, url: URL.createObjectURL(blob) };
}

/**
 * Datei-Download mit Bearer-Token.
 *
 * `api.get` kann das nicht: es liest die Antwort als Text und würde eine DOCX
 * zu Zeichensalat machen. Ein `<a href>` kann es auch nicht — der Token steckt
 * im localStorage, nicht im Cookie, und ohne ihn antwortet der Server 401.
 *
 * Gibt `false` zurück, wenn der Server den Endpunkt (noch) nicht kennt; der
 * Aufrufer sagt dem Nutzer dann, woran es liegt.
 */
export async function ladeDatei(
  pfad: string,
  dateiname: string
): Promise<{ ok: boolean; status: number; text?: string }> {
  const token = authToken();
  const r = await fetch(apiUrl(pfad), {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!r.ok) {
    return { ok: false, status: r.status, text: await r.text().catch(() => "") };
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Erst nach dem Klick freigeben — sonst ist die Blob-URL beim Speichern weg.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true, status: r.status };
}
